<template>
  <BaseInput
    v-bind="props"
    :class="props.class"
    :rules="mergedRules"
    v-on="
      reEmit(emit, ['update:loading', 'validationError', 'update:modelValue'])
    "
  >
    <template #input="{ id, describedBy, errorId, listeners, displayInvalid }">
      <PInputText
        v-bind="$attrs"
        :id
        v-model="modelValue"
        :aria-describedby="describedBy"
        :aria-errormessage="errorId"
        :aria-labelledby="ariaLabelledby"
        :disabled
        fluid
        :invalid="displayInvalid"
        :maxlength
        :minlength
        :name
        :pt="assign({ root: { name } }, pt?.pcInput ?? {})"
        :required
        :size
        v-on="listeners"
        @focus="emit('focus', name)"
      />
    </template>
    <template v-for="(_index, slotName) in $slots" #[slotName]="data">
      <slot :name="slotName" v-bind="data" />
    </template>
  </BaseInput>
</template>

<script setup lang="ts">
import type { InputTextPassThroughOptions } from "primevue";

import { assign } from "radash";
import { computed } from "vue";

import type { InferTextInputRulesProps } from "@solvari/common-fe/validation";

import { useModel } from "@solvari/common-fe/composables";
import { reEmit } from "@solvari/common-fe/helpers";
import { inferTextInputRules } from "@solvari/common-fe/validation";

import type {
  BaseInputEmits,
  BaseInputPassThroughOptions,
  BaseInputProps,
} from "@/molecules/baseInput.ts";

import PInputText from "@/atoms/PInputText.vue";
import BaseInput from "@/molecules/BaseInput.vue";

const props = defineProps<
  BaseInputProps<string | null | undefined> &
    InferTextInputRulesProps & {
      class?: string;
      pt?: BaseInputPassThroughOptions & {
        pcInput?: InputTextPassThroughOptions;
      };
    }
>();
const emit = defineEmits<
  BaseInputEmits & {
    (event: "focus", name: string): void;
    (event: "update:modelValue", value: string | null | undefined): void;
  }
>();

const modelValue = useModel("modelValue", props, emit);

const inferredRules = inferTextInputRules(props);
const mergedRules = computed(() =>
  props.rules ? [...inferredRules.value, ...props.rules] : inferredRules.value,
);
</script>
